/**
 * studioService.ts
 * API service for Studio features: Interfaces, Triggers, and Apps.
 * Uses the apiFetch helper which automatically attaches the Supabase JWT.
 */
import { apiFetch } from '../lib/api'
import type { InterfaceBuildContract, InterfaceValidationResult, TriggerWorkflowContract } from '../types'

import { API_BASE } from '@/lib/config'

// ── Types ─────────────────────────────────────────────────────────────────────

export interface InterfaceFile {
  path: string
  content: string
  language: string
}

export interface Interface {
  id: string
  user_id: string
  project_id?: string
  conversation_id?: string
  name: string
  description?: string
  files: InterfaceFile[]
  entry_file: string
  sandbox_id?: string
  sandbox_url?: string
  sandbox_expires_at?: string
  storage_key?: string
  storage_url?: string
  status: 'draft' | 'building' | 'stabilizing' | 'ready' | 'error'
  build_error?: string
  framework: string
  thumbnail_url?: string
  created_at: string
  updated_at: string
}

export interface Trigger {
  id: string
  user_id: string
  project_id?: string
  conversation_id?: string
  name: string
  description?: string
  trigger_type: 'gmail' | 'calendar' | 'schedule' | 'webhook' | 'manual'
  trigger_config: Record<string, unknown>
  action_type: 'run_agent' | 'send_message' | 'run_workflow'
  action_config: Record<string, unknown>
  workflow_contract?: TriggerWorkflowContract | null
  verification_contract?: Array<{ label: string; method: string; target?: string }> | null
  is_active: boolean
  last_triggered_at?: string
  trigger_count: number
  last_error?: string
  created_at: string
  updated_at: string
}

export interface TriggerRun {
  id: string
  trigger_id: string
  user_id: string
  input_data: Record<string, unknown>
  status: 'running' | 'completed' | 'failed' | 'skipped'
  output_data: Record<string, unknown>
  error_message?: string
  agent_run_id?: string
  started_at: string
  completed_at?: string
  duration_ms?: number
}

export interface App {
  id: string
  user_id: string
  interface_id?: string
  name: string
  description?: string
  icon: string
  is_published: boolean
  published_url?: string
  custom_domain?: string
  settings: Record<string, unknown>
  view_count: number
  created_at: string
  updated_at: string
}

// ── Interfaces API ────────────────────────────────────────────────────────────

export const interfacesApi = {
  async list(conversationId?: string): Promise<Interface[]> {
    const url = conversationId
      ? `${API_BASE}/api/interfaces?conversation_id=${conversationId}`
      : `${API_BASE}/api/interfaces`
    const res = await apiFetch(url)
    if (!res.ok) throw new Error('Kunne ikke hente grensesnitt')
    const data = await res.json()
    return data.interfaces || []
  },

  async create(params: {
    name?: string
    description?: string
    files?: InterfaceFile[]
    entry_file?: string
    framework?: string
    conversation_id?: string
    project_id?: string
  }): Promise<Interface> {
    const res = await apiFetch(`${API_BASE}/api/interfaces`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
    })
    if (!res.ok) throw new Error('Kunne ikke opprette grensesnitt')
    return res.json()
  },

  async get(id: string): Promise<Interface> {
    const res = await apiFetch(`${API_BASE}/api/interfaces/${id}`)
    if (!res.ok) throw new Error('Grensesnitt ikke funnet')
    return res.json()
  },

  async update(id: string, params: Partial<Interface>): Promise<Interface> {
    const res = await apiFetch(`${API_BASE}/api/interfaces/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
    })
    if (!res.ok) throw new Error('Kunne ikke oppdatere grensesnitt')
    return res.json()
  },

  async delete(id: string): Promise<void> {
    const res = await apiFetch(`${API_BASE}/api/interfaces/${id}`, { method: 'DELETE' })
    if (!res.ok) throw new Error('Kunne ikke slette grensesnitt')
  },

  async createSandbox(id: string): Promise<{ sandbox_id: string; sandbox_url: string; expires_at: string }> {
    const res = await apiFetch(`${API_BASE}/api/interfaces/${id}/sandbox`, { method: 'POST' })
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      throw new Error(err.detail || 'Kunne ikke starte sandbox')
    }
    return res.json()
  },

  /**
   * Generate app code with Claude and start a live E2B preview.
   * Returns the preview URL (E2B hosted Vite dev server).
   */
  async generatePreview(id: string, prompt: string): Promise<{
    preview_url: string
    code: string
    status: string
    contract?: InterfaceBuildContract
    validation?: InterfaceValidationResult
    user_message?: string
  }> {
    const res = await apiFetch(`${API_BASE}/api/interfaces/${id}/generate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ prompt }),
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      throw new Error(err.detail || 'Kodegenerering feilet')
    }
    const data = await res.json()
    if (data.validation) {
      data.validation = {
        ...data.validation,
        previewVerified: data.validation.previewVerified ?? data.validation.preview_verified,
        verificationSummary: data.validation.verificationSummary ?? data.validation.verification_summary,
      }
    }
    return data
  },

  /**
   * Update files in an existing E2B sandbox (live editing).
   * Vite HMR will auto-refresh the preview.
   */
  async updateCode(id: string, files: Record<string, string>): Promise<{
    status: string
    files_updated: string[]
    validation?: InterfaceValidationResult
    user_message?: string
  }> {
    const res = await apiFetch(`${API_BASE}/api/interfaces/${id}/update-code`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ files }),
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      throw new Error(err.detail || 'Oppdatering feilet')
    }
    const data = await res.json()
    if (data.validation) {
      data.validation = {
        ...data.validation,
        previewVerified: data.validation.previewVerified ?? data.validation.preview_verified,
        verificationSummary: data.validation.verificationSummary ?? data.validation.verification_summary,
      }
    }
    return data
  },

  /**
   * Get the current preview URL for an interface.
   */
  async getPreview(id: string): Promise<{ preview_url: string; status: string }> {
    const res = await apiFetch(`${API_BASE}/api/interfaces/${id}/preview`)
    if (!res.ok) throw new Error('Kunne ikke hente preview')
    return res.json()
  },
}

// ── Triggers API ──────────────────────────────────────────────────────────────

export const triggersApi = {
  async list(projectId?: string): Promise<Trigger[]> {
    const url = projectId
      ? `${API_BASE}/api/triggers?project_id=${encodeURIComponent(projectId)}`
      : `${API_BASE}/api/triggers`
    const res = await apiFetch(url)
    if (!res.ok) throw new Error('Kunne ikke hente triggers')
    const data = await res.json()
    return data.triggers || []
  },

  async create(params: {
    name: string
    description?: string
    trigger_type: string
    trigger_config?: Record<string, unknown>
    action_type?: string
    action_config?: Record<string, unknown>
    project_id?: string
    conversation_id?: string
    workflow_contract?: TriggerWorkflowContract | null
    verification_contract?: Array<{ label: string; method: string; target?: string }> | null
  }): Promise<Trigger> {
    const res = await apiFetch(`${API_BASE}/api/triggers`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      throw new Error(err.detail || 'Kunne ikke opprette trigger')
    }
    return res.json()
  },

  async update(id: string, params: Partial<Trigger>): Promise<Trigger> {
    const res = await apiFetch(`${API_BASE}/api/triggers/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
    })
    if (!res.ok) throw new Error('Kunne ikke oppdatere trigger')
    return res.json()
  },

  async delete(id: string): Promise<void> {
    const res = await apiFetch(`${API_BASE}/api/triggers/${id}`, { method: 'DELETE' })
    if (!res.ok) throw new Error('Kunne ikke slette trigger')
  },

  async toggle(id: string): Promise<{ is_active: boolean }> {
    const res = await apiFetch(`${API_BASE}/api/triggers/${id}/toggle`, { method: 'POST' })
    if (!res.ok) throw new Error('Kunne ikke endre trigger-status')
    return res.json()
  },

  async getRuns(id: string, limit = 20): Promise<TriggerRun[]> {
    const res = await apiFetch(`${API_BASE}/api/triggers/${id}/runs?limit=${limit}`)
    if (!res.ok) throw new Error('Kunne ikke hente trigger-historikk')
    const data = await res.json()
    return data.runs || []
  },
}

// ── Apps API ──────────────────────────────────────────────────────────────────

export const appsApi = {
  async list(conversationId?: string): Promise<App[]> {
    const url = conversationId
      ? `${API_BASE}/api/apps?conversation_id=${encodeURIComponent(conversationId)}`
      : `${API_BASE}/api/apps`
    const res = await apiFetch(url)
    if (!res.ok) throw new Error('Kunne ikke hente apper')
    const data = await res.json()
    return data.apps || []
  },

  async create(params: {
    name: string
    description?: string
    icon?: string
    interface_id?: string
  }): Promise<App> {
    const res = await apiFetch(`${API_BASE}/api/apps`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
    })
    if (!res.ok) throw new Error('Kunne ikke opprette app')
    return res.json()
  },

  async update(id: string, params: Partial<App>): Promise<App> {
    const res = await apiFetch(`${API_BASE}/api/apps/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
    })
    if (!res.ok) throw new Error('Kunne ikke oppdatere app')
    return res.json()
  },

  async delete(id: string): Promise<void> {
    const res = await apiFetch(`${API_BASE}/api/apps/${id}`, { method: 'DELETE' })
    if (!res.ok) throw new Error('Kunne ikke slette app')
  },

  async publish(id: string): Promise<{ published: boolean; url: string }> {
    const res = await apiFetch(`${API_BASE}/api/apps/${id}/publish`, { method: 'POST' })
    if (!res.ok) throw new Error('Kunne ikke publisere app')
    return res.json()
  },
}
// ── Sine Pages ────────────────────────────────────────────────────────────────
export interface SinePage {
  id: string
  title: string
  slug: string
  description?: string
  cover_image?: string
  tags?: string[]
  published: boolean
  public_url?: string
  conversation_id?: string
  created_at: string
  updated_at: string
}

export const pagesService = {
  async publish(params: {
    title: string
    content: string
    description?: string
    cover_image?: string
    tags?: string[]
    conversation_id?: string
    custom_slug?: string
  }): Promise<{ id: string; slug: string; public_url: string; title: string }> {
    const res = await apiFetch(`${API_BASE}/api/pages`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
    })
    if (!res.ok) throw new Error('Kunne ikke publisere siden')
    return res.json()
  },

  async list(): Promise<SinePage[]> {
    const res = await apiFetch(`${API_BASE}/api/pages`)
    if (!res.ok) return []
    const data = await res.json()
    return data.pages || []
  },

  async get(slug: string): Promise<SinePage | null> {
    const res = await fetch(`${API_BASE}/api/pages/${slug}`)
    if (!res.ok) return null
    return res.json()
  },

  async delete(id: string): Promise<void> {
    const res = await apiFetch(`${API_BASE}/api/pages/${id}`, { method: 'DELETE' })
    if (!res.ok) throw new Error('Kunne ikke slette siden')
  },

  async unpublish(id: string): Promise<void> {
    const res = await apiFetch(`${API_BASE}/api/pages/${id}/unpublish`, { method: 'POST' })
    if (!res.ok) throw new Error('Kunne ikke avpublisere siden')
  },
}

// ── Mail Sine ─────────────────────────────────────────────────────────────────
export const mailService = {
  async getAddress(): Promise<{ address: string; domain: string }> {
    const res = await apiFetch(`${API_BASE}/api/mail/address`)
    if (!res.ok) throw new Error('Kunne ikke hente e-postadresse')
    return res.json()
  },

  async listTasks(): Promise<{ id: string; sender: string; subject: string; status: string; created_at: string }[]> {
    const res = await apiFetch(`${API_BASE}/api/mail/tasks`)
    if (!res.ok) return []
    const data = await res.json()
    return data.tasks || []
  },

  async testMail(params: { subject: string; body: string; sender?: string }): Promise<{ status: string; task_preview: string }> {
    const res = await apiFetch(`${API_BASE}/api/mail/test`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(params),
    })
    if (!res.ok) throw new Error('Test feilet')
    return res.json()
  },
}

// ── Slack ─────────────────────────────────────────────────────────────────────
export const slackService = {
  async getStatus(): Promise<{ connected: boolean; team_name?: string; team_id?: string; connected_at?: string }> {
    const res = await apiFetch(`${API_BASE}/api/slack/status`)
    if (!res.ok) return { connected: false }
    return res.json()
  },

  async startOAuth(): Promise<{ url: string }> {
    const res = await apiFetch(`${API_BASE}/api/slack/oauth/start`)
    if (!res.ok) throw new Error('Kunne ikke starte Slack OAuth')
    return res.json()
  },

  async disconnect(): Promise<void> {
    const res = await apiFetch(`${API_BASE}/api/slack/disconnect`, { method: 'DELETE' })
    if (!res.ok) throw new Error('Kunne ikke koble fra Slack')
  },
}
